import { noop } from '@socialive/common';
import { Button, FileUpload, Form, FormItemLabel, Hyperlink, InfoIcon, SelectField } from '@socialive/ui';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectAccountChannelsSorted, uploadAssets } from '~/store';

const RECORDINGS_CHANNEL_INPUT_NAME = 'RecordingsUploadChannel';

const UploadForm = () => {
  const dispatch = useDispatch();
  const accountChannels = useSelector(selectAccountChannelsSorted);

  const channelOptions = useMemo(
    () => accountChannels.map(({ token, name }) => ({ value: token, label: name })),
    [accountChannels]
  );

  const form = useForm({ defaultValues: { [RECORDINGS_CHANNEL_INPUT_NAME]: channelOptions[0]?.value ?? '' } });
  const { watch } = form;

  const selectedChannel = watch(RECORDINGS_CHANNEL_INPUT_NAME, channelOptions[0]?.value ?? '');

  const onUpload = useCallback(
    async (event) => {
      const { files } = event.target;
      if (!files?.length) return;

      const filteredFiles = Array.from(files).filter((file) => {
        if (!file.type.includes('video/')) {
          toast.error(`${file.name} has an invalid format`);
          return false;
        }

        return true;
      });

      await dispatch(
        uploadAssets({ files: filteredFiles, channelToken: selectedChannel, trigger: 'UploadMediaModal@onUpload' })
      );
    },
    [dispatch, selectedChannel]
  );

  return (
    <Form form={form} onSubmit={noop} className="flex flex-col gap-2 m-0.5 pb-25">
      <SelectField name={RECORDINGS_CHANNEL_INPUT_NAME} options={channelOptions} label="SELECT A CHANNEL" />
      <div className="mt-2">
        <FormItemLabel label="SELECT RECORDINGS" />
        <FileUpload
          multiple
          accept="video/H264,*/flv,video/mp4,video/mpeg,video/quicktime"
          name="RecordingsUpload"
          onChange={onUpload}
        >
          <Button className="mt-1" size="large" context="subtle">
            Upload Files
          </Button>
        </FileUpload>
      </div>
      <span className="flex gap-1 mt-2 items-center">
        Supported Formats include mp4, webm, mov. ProRes is not supported.
        <Hyperlink
          href="https://go.socialive.us/support-center/upload-recordings-1"
          target="support"
          rel="noopener noreferrer"
        >
          <InfoIcon />
        </Hyperlink>
      </span>
    </Form>
  );
};

export default UploadForm;
